/**
 * JobReturnPage — Sprint S7 · 2026-02c
 *
 * Minimal form for logging leftover stock from a completed job. Ticklist
 * shape · every inventory item is a row · operator enters qty for any
 * row they want to credit · POSTs the batch to /api/inventory/job-return
 * with `ref_type='appraisal'` and `ref_id=appraisalId`.
 *
 * URL: /admin/job-return/:appraisalId
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Undo2, Loader2, CheckCircle2 } from 'lucide-react';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
});

export default function JobReturnPage() {
  const { appraisalId } = useParams();
  const [items, setItems] = useState([]);
  const [qtys, setQtys] = useState({});          // inventory_item_id → qty string
  const [notes, setNotes] = useState({});        // inventory_item_id → notes
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [err, setErr] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get('/inventory');
      setItems((data || []).filter(i => i.active !== false));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, []);
  useEffect(() => { loadItems(); }, [loadItems]);

  const validRows = useMemo(() =>
    items
      .filter(i => Number(qtys[i._id]) > 0)
      .map(i => ({
        inventory_item_id: i._id,
        delta:             Number(qtys[i._id]),
        notes:             notes[i._id] || '',
      }))
  , [items, qtys, notes]);

  const submit = async () => {
    if (validRows.length === 0) return;
    setSaving(true);
    setErr(null);
    setFlash(null);
    try {
      const { data } = await api.post('/inventory/job-return', {
        appraisal_id: appraisalId,
        rows:         validRows,
      });
      setFlash(`Returned ${data.count} item${data.count === 1 ? '' : 's'} to stock`);
      setQtys({});
      setNotes({});
      await loadItems();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-return-page">
      <div className="max-w-4xl mx-auto p-6">
        <Link to="/" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft size={12} /> Back
        </Link>

        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          <div>
            <h1 className="text-lg font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
              <Undo2 size={16} className="text-emerald-700" /> Job Return
            </h1>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Log leftover stock the crew brings back from appraisal <code className="text-[10px] bg-slate-100 px-1 rounded">{appraisalId}</code>. Every row credits stock and writes a canonical <code className="text-[10px] bg-slate-100 px-1 rounded">job_return</code> movement.
            </p>
            <p className="text-[11px] text-amber-700 mt-1">
              Part-buckets are valuable · flag partial returns in Notes so the office knows what's a fresh tub vs a half-used one.
            </p>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-xs" data-testid="job-return-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left p-2">SKU</th>
                  <th className="text-left p-2">Item</th>
                  <th className="text-right p-2 w-24">Qty</th>
                  <th className="text-left p-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="4" className="p-4 text-center text-slate-400 italic">No active inventory items · seed real items first via <code className="text-[10px] bg-slate-100 px-1 rounded">POST /admin/inventory/seed-real-items</code>.</td></tr>
                )}
                {items.map((it, i) => (
                  <tr
                    key={it._id}
                    className={i % 2 ? 'bg-white' : 'bg-slate-50/50'}
                    data-testid={`job-return-row-${it.sku || i}`}
                  >
                    <td className="p-2 font-mono-num text-slate-500 text-[10px]">{it.sku || '—'}</td>
                    <td className="p-2 text-slate-800">
                      {it.name}
                      {it.is_leftover && <span className="ml-1 text-[9px] px-1 rounded bg-amber-100 text-amber-800 uppercase tracking-wider">leftover</span>}
                      {it.colour_code && <span className="ml-1 text-[9px] px-1 rounded bg-sky-100 text-sky-800 uppercase tracking-wider">{it.colour_code}</span>}
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={qtys[it._id] || ''}
                        onChange={e => setQtys(q => ({ ...q, [it._id]: e.target.value }))}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono-num text-right"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        placeholder="part bucket · used 30% · etc."
                        value={notes[it._id] || ''}
                        onChange={e => setNotes(n => ({ ...n, [it._id]: e.target.value }))}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-xs"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {err && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-2 text-sm" data-testid="job-return-error">{err}</div>
          )}
          {flash && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-2 text-sm inline-flex items-center gap-1" data-testid="job-return-flash">
              <CheckCircle2 size={14} /> {flash}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-100">
            <span className="text-[11px] text-slate-500">
              {validRows.length} row{validRows.length === 1 ? '' : 's'} to return
            </span>
            <button
              data-testid="job-return-submit"
              onClick={submit}
              disabled={validRows.length === 0 || saving}
              className="px-3 py-1.5 bg-emerald-700 text-white rounded text-sm font-semibold hover:bg-emerald-800 disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}
              Credit stock
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
